export type AttributionKey =
  | "utm_source"
  | "utm_medium"
  | "utm_campaign"
  | "utm_content"
  | "utm_term"
  | "gclid"
  | "gbraid"
  | "wbraid"
  | "fbclid"
  | "msclkid"
  | "ref";

export type AttributionTouchpoint = Partial<Record<AttributionKey, string>> & {
  captured_at: string;
  page_url: string;
  page_path: string;
  page_title: string;
  referrer: string;
};

export type AttributionSnapshot = {
  first: AttributionTouchpoint | null;
  last: AttributionTouchpoint | null;
  current: AttributionTouchpoint | null;
};

const STORAGE_KEY = "vw_attribution_v1";
const ATTRIBUTION_KEYS: AttributionKey[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ref"
];

function readStoredSnapshot(): AttributionSnapshot {
  if (typeof window === "undefined") return { first: null, last: null, current: null };

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { first: null, last: null, current: null };
    const parsed = JSON.parse(raw) as Partial<AttributionSnapshot>;
    return {
      first: parsed.first ?? null,
      last: parsed.last ?? null,
      current: parsed.current ?? null
    };
  } catch {
    return { first: null, last: null, current: null };
  }
}

function writeStoredSnapshot(snapshot: AttributionSnapshot) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Best-effort attribution. Private browsing/storage blocks should not break UX.
  }
}

function hasExternalReferrer(referrer: string) {
  if (typeof window === "undefined" || !referrer) return false;

  try {
    return new URL(referrer).hostname !== window.location.hostname;
  } catch {
    return false;
  }
}

function hasCampaignSignal(touchpoint: AttributionTouchpoint) {
  return ATTRIBUTION_KEYS.some((key) => Boolean(touchpoint[key])) || hasExternalReferrer(touchpoint.referrer);
}

function currentTouchpoint(): AttributionTouchpoint {
  const params = new URLSearchParams(window.location.search);
  const touchpoint: AttributionTouchpoint = {
    captured_at: new Date().toISOString(),
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    referrer: document.referrer || ""
  };

  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (value) touchpoint[key] = value.slice(0, 300);
  }

  return touchpoint;
}

export function captureAttributionFromUrl(): AttributionSnapshot {
  if (typeof window === "undefined") return { first: null, last: null, current: null };

  const stored = readStoredSnapshot();
  const current = currentTouchpoint();
  const first = stored.first ?? current;
  const last = hasCampaignSignal(current) ? current : stored.last ?? first;
  const snapshot = { first, last, current };
  writeStoredSnapshot(snapshot);

  return snapshot;
}

export function getAttributionSnapshot(): AttributionSnapshot {
  return readStoredSnapshot();
}

export function attributionDataLayerParams(snapshot: AttributionSnapshot): Record<string, string> {
  const params: Record<string, string> = {};

  for (const [prefix, touchpoint] of [
    ["first", snapshot.first],
    ["last", snapshot.last]
  ] as const) {
    if (!touchpoint) continue;

    params[`attribution_${prefix}_page_path`] = touchpoint.page_path;
    params[`attribution_${prefix}_referrer`] = touchpoint.referrer;

    for (const key of ATTRIBUTION_KEYS) {
      const value = touchpoint[key];
      if (value) params[`attribution_${prefix}_${key}`] = value;
    }
  }

  return params;
}
